import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { Building2, Users, UserCheck, ChevronDown, ChevronRight, Activity, LayoutDashboard } from 'lucide-react';
import { getCompanies, getCompanyUserInvitations, getAppUserByEmail, getAppEventsByUser, getActiveUsersCount } from '@/lib/firebase';
import { useAuth } from '@/contexts/auth-context';
import { logger } from '@/lib/logger';

interface Company {
  id: string;
  name: string;
  enabled?: boolean;
  trialExpiryDate?: any;
  trialExtendedCount?: number;
  type?: string;
}

interface Invitation {
  id: string;
  email: string;
  companyId?: string;
  role?: string;
  status?: string;
}

const toDate = (ts: any): Date | null => {
  if (!ts) return null;
  try { return ts.toDate ? ts.toDate() : new Date(ts); } catch { return null; }
};

const formatDate = (ts: any): string => {
  const d = toDate(ts);
  if (!d) return '—';
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
};

const isAfterNow = (ts: any): boolean => {
  const d = toDate(ts);
  return d ? d > new Date() : false;
};

export default function SalesDashboard() {
  const { user } = useAuth();

  // Raw data
  const [companies, setCompanies] = useState<Company[]>([]);
  const [invitationsCount, setInvitationsCount] = useState<number | null>(null);
  const [activeUsersCount, setActiveUsersCount] = useState<number | null>(null);
  const [loadingCompanies, setLoadingCompanies] = useState(true);

  // Trials UI state
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const [trialUsers, setTrialUsers] = useState<Record<string, Invitation[]>>({});
  const [loadingUsers, setLoadingUsers] = useState<Set<string>>(new Set());
  const [expiredLimit, setExpiredLimit] = useState(10);

  // Activity modal
  const [activityUser, setActivityUser] = useState<{ email: string; companyName: string } | null>(null);
  const [activityEvents, setActivityEvents] = useState<Record<string, number>>({});
  const [activityLoading, setActivityLoading] = useState(false);

  // --- Fetch companies ---
  useEffect(() => {
    if (!user) return;
    const loadData = async () => {
      try {
        const data = await getCompanies();
        setCompanies(data as Company[]);
      } catch (err) {
        logger.error('Error loading companies:', err);
      } finally {
        setLoadingCompanies(false);
      }
    };
    loadData();
    const timer = setInterval(loadData, 15_000);
    return () => clearInterval(timer);
  }, [user]);

  // --- Fetch invitation count ---
  useEffect(() => {
    if (!user) return;
    getCompanyUserInvitations()
      .then(data => setInvitationsCount(data.length))
      .catch(() => setInvitationsCount(0));
  }, [user]);

  // --- Fetch active users count ---
  useEffect(() => {
    if (!user) return;
    getActiveUsersCount()
      .then(count => setActiveUsersCount(count))
      .catch(() => setActiveUsersCount(0));
  }, [user]);

  // --- Derived counts ---
  const totalCustomers = companies.filter(c => c.enabled === true && !c.trialExpiryDate).length;

  const activeTrials = companies
    .filter(c => c.trialExpiryDate && isAfterNow(c.trialExpiryDate))
    .sort((a, b) => {
      const da = toDate(a.trialExpiryDate)?.getTime() ?? 0;
      const db2 = toDate(b.trialExpiryDate)?.getTime() ?? 0;
      return da - db2;
    });

  const expiredTrials = companies
    .filter(c => c.trialExpiryDate && !isAfterNow(c.trialExpiryDate))
    .sort((a, b) => {
      const da = toDate(a.trialExpiryDate)?.getTime() ?? 0;
      const db2 = toDate(b.trialExpiryDate)?.getTime() ?? 0;
      return db2 - da;
    })
    .slice(0, expiredLimit);

  // --- Toggle trial row expand ---
  const toggleExpand = async (company: Company) => {
    const next = new Set(expandedIds);
    if (next.has(company.id)) {
      next.delete(company.id);
      setExpandedIds(next);
      return;
    }
    next.add(company.id);
    setExpandedIds(next);

    if (trialUsers[company.id]) return; // already fetched

    setLoadingUsers(prev => new Set(prev).add(company.id));
    try {
      const invitations = await getCompanyUserInvitations(company.id);
      setTrialUsers(prev => ({
        ...prev,
        [company.id]: invitations as Invitation[],
      }));
    } catch (err) {
      logger.error('Error loading trial users:', err);
      setTrialUsers(prev => ({ ...prev, [company.id]: [] }));
    } finally {
      setLoadingUsers(prev => { const s = new Set(prev); s.delete(company.id); return s; });
    }
  };

  // --- Load app events for a user ---
  // appEvents.uid = appUsers document ID, so look up appUsers by email first
  const openActivity = async (email: string, companyName: string) => {
    setActivityUser({ email, companyName });
    setActivityEvents({});
    setActivityLoading(true);
    try {
      const grouped: Record<string, number> = {};

      // Step 1: find the appUsers doc ID for this email
      const appUser = await getAppUserByEmail(email).catch(() => null);

      if (!appUser) {
        setActivityEvents({});
        return;
      }

      const appUserUid = appUser.id;

      // Step 2: query appEvents with that doc ID
      const events = await getAppEventsByUser(appUserUid).catch(() => []);

      events.forEach((data: any) => {
        const key = data.eventType ?? data.type ?? data.action ?? data.event ?? data.name ?? 'unknown';
        grouped[key] = (grouped[key] ?? 0) + 1;
      });
      setActivityEvents(grouped);
    } catch {
      setActivityEvents({});
    } finally {
      setActivityLoading(false);
    }
  };

  // --- Stat card skeleton ---
  const StatCard = ({ label, value, icon: Icon, color }: {
    label: string; value: number | null; icon: any; color: string;
  }) => (
    <Card>
      <CardContent className="p-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-slate-500 mb-1">{label}</p>
            {value === null
              ? <Skeleton className="h-8 w-16" />
              : <p className="text-3xl font-bold text-slate-900">{value.toLocaleString()}</p>
            }
          </div>
          <div className={`p-3 rounded-full ${color}`}>
            <Icon className="h-6 w-6" />
          </div>
        </div>
      </CardContent>
    </Card>
  );

  // --- Trial table row ---
  const TrialRow = ({ company, isExpired = false }: { company: Company; isExpired?: boolean }) => {
    const extended = (company.trialExtendedCount ?? 0) > 0;
    const expanded = expandedIds.has(company.id);
    const users = trialUsers[company.id] ?? [];
    const fetchingUsers = loadingUsers.has(company.id);

    return (
      <>
        <TableRow
          className={`cursor-pointer hover:bg-slate-50 ${extended && !isExpired ? 'bg-amber-50' : ''}`}
          onClick={() => toggleExpand(company)}
        >
          <TableCell className="w-8 p-2">
            {expanded
              ? <ChevronDown className="h-4 w-4 text-slate-400" />
              : <ChevronRight className="h-4 w-4 text-slate-400" />}
          </TableCell>
          <TableCell>
            <div className="flex items-center gap-2">
              <span className="font-medium">{company.name || 'Unnamed'}</span>
              {extended && (
                <Badge className="bg-amber-100 text-amber-800 border-amber-200 text-xs">Extended ×{company.trialExtendedCount}</Badge>
              )}
            </div>
          </TableCell>
          <TableCell className="text-slate-600">{formatDate(company.trialExpiryDate)}</TableCell>
          <TableCell>
            <Badge className={
              isExpired
                ? 'bg-red-100 text-red-800 border-red-200'
                : 'bg-green-100 text-green-800 border-green-200'
            }>
              {isExpired ? 'Expired' : 'Active'}
            </Badge>
          </TableCell>
        </TableRow>

        {expanded && (
          <TableRow className="bg-slate-50 hover:bg-slate-50">
            <TableCell />
            <TableCell colSpan={3} className="py-3 px-4">
              {fetchingUsers ? (
                <div className="space-y-2">
                  <Skeleton className="h-4 w-48" />
                  <Skeleton className="h-4 w-40" />
                </div>
              ) : users.length === 0 ? (
                <p className="text-sm text-slate-400 italic">No invited users</p>
              ) : (
                <div className="space-y-1.5">
                  {users.map(u => (
                    <div key={u.id} className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-slate-700">{u.email}</span>
                        {u.role && (
                          <Badge variant="secondary" className="text-xs">{u.role}</Badge>
                        )}
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 px-2 text-xs text-slate-500 hover:text-slate-800 gap-1"
                        onClick={e => { e.stopPropagation(); openActivity(u.email, company.name); }}
                      >
                        <Activity className="h-3.5 w-3.5" />
                        View Activity
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </TableCell>
          </TableRow>
        )}
      </>
    );
  };

  return (
    <div className="flex-1 p-8">
      <div className="max-w-5xl mx-auto space-y-8">

        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <LayoutDashboard className="h-6 w-6" />
            Dashboard
          </h1>
          <p className="text-slate-500 mt-1">Overview of customers and trials</p>
        </div>

        {/* Customers Section */}
        <section>
          <h2 className="text-lg font-semibold text-slate-700 mb-4">Customers</h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <StatCard
              label="Total Customers"
              value={loadingCompanies ? null : totalCustomers}
              icon={Building2}
              color="bg-blue-50 text-blue-600"
            />
            <StatCard
              label="Total Invited Users"
              value={invitationsCount}
              icon={Users}
              color="bg-purple-50 text-purple-600"
            />
            <StatCard
              label="Total Active Users"
              value={activeUsersCount}
              icon={UserCheck}
              color="bg-green-50 text-green-600"
            />
          </div>
        </section>

        {/* Trials Section */}
        <section className="space-y-6">
          <h2 className="text-lg font-semibold text-slate-700">Trials</h2>

          {/* Active Trials */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                Active Trials
                <Badge variant="secondary">{activeTrials.length}</Badge>
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {loadingCompanies ? (
                <div className="p-6 space-y-3">
                  {[1,2,3].map(i => <Skeleton key={i} className="h-10 w-full" />)}
                </div>
              ) : activeTrials.length === 0 ? (
                <p className="text-slate-400 text-sm text-center py-8">No active trials</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-8" />
                      <TableHead>Company</TableHead>
                      <TableHead>Expiry Date</TableHead>
                      <TableHead>Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {activeTrials.map(c => <TrialRow key={c.id} company={c} />)}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>

          {/* Recently Expired Trials */}
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base flex items-center gap-2">
                  Recently Expired
                  <Badge variant="secondary">{expiredTrials.length}</Badge>
                </CardTitle>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-slate-500">Showing</span>
                  <span className="text-sm font-medium">{expiredLimit}</span>
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-7 text-xs"
                    onClick={() => setExpiredLimit(n => n + 10)}
                  >
                    Show more
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              {loadingCompanies ? (
                <div className="p-6 space-y-3">
                  {[1,2,3].map(i => <Skeleton key={i} className="h-10 w-full" />)}
                </div>
              ) : expiredTrials.length === 0 ? (
                <p className="text-slate-400 text-sm text-center py-8">No expired trials</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-8" />
                      <TableHead>Company</TableHead>
                      <TableHead>Expired On</TableHead>
                      <TableHead>Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {expiredTrials.map(c => <TrialRow key={c.id} company={c} isExpired />)}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </section>
      </div>

      {/* App Events Modal */}
      <Dialog open={!!activityUser} onOpenChange={open => { if (!open) setActivityUser(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Activity Summary</DialogTitle>
          </DialogHeader>
          {activityUser && (
            <div className="space-y-4">
              <div>
                <p className="text-sm text-slate-500">User</p>
                <p className="font-medium text-slate-900">{activityUser.email}</p>
              </div>
              <div>
                <p className="text-sm text-slate-500 mb-2">Company</p>
                <p className="text-sm text-slate-700">{activityUser.companyName}</p>
              </div>
              {activityLoading ? (
                <div className="space-y-2">
                  {[1,2,3].map(i => <Skeleton key={i} className="h-8 w-full" />)}
                </div>
              ) : Object.keys(activityEvents).length === 0 ? (
                <p className="text-slate-400 text-sm text-center py-4">No activity recorded</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Event Type</TableHead>
                      <TableHead className="text-right">Count</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {Object.entries(activityEvents)
                      .sort(([,a],[,b]) => b - a)
                      .map(([type, count]) => (
                        <TableRow key={type}>
                          <TableCell className="font-medium text-sm">{type}</TableCell>
                          <TableCell className="text-right">
                            <Badge variant="secondary">{count}</Badge>
                          </TableCell>
                        </TableRow>
                      ))
                    }
                  </TableBody>
                </Table>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
